import { useCallback, useEffect, useMemo, useState } from 'react';

import { LEXICON_API_URL } from '../config/api';

let lexiconCache = null;
let lexiconFetchPromise = null;

/**
 * Fetch lexicon once; subsequent calls return cached result.
 * @returns {Promise<Array<{id: number, title: string}>>}
 */
const fetchLexiconOnce = () => {
  if (lexiconCache) return Promise.resolve(lexiconCache);
  if (lexiconFetchPromise) return lexiconFetchPromise;
  lexiconFetchPromise = fetch(`${LEXICON_API_URL}/lexic?service=Platform&lang=en`)
    .then((res) => res.json())
    .then((data) => {
      lexiconCache = Array.isArray(data) ? data : [];
      return lexiconCache;
    })
    .catch((err) => {
      lexiconFetchPromise = null;
      console.warn('Lexicon fetch failed:', err?.message);
      return [];
    });
  return lexiconFetchPromise;
};

const normalize = (s) =>
  (s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const parseLexiconId = (value) => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      const numeric = Number(trimmed);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
    }
  }

  return null;
};

const getDirectLexiconId = (parameterInput) => {
  if (!parameterInput || typeof parameterInput !== 'object') {
    return null;
  }

  const directValue = parameterInput.lexic ?? parameterInput.lexic_id ?? parameterInput.lexc;
  return parseLexiconId(directValue);
};

const getParameterNameFromInput = (parameterInput) => {
  if (typeof parameterInput === 'string') {
    return parameterInput;
  }

  if (!parameterInput || typeof parameterInput !== 'object') {
    return '';
  }

  return (
    parameterInput.parameter_name ||
    parameterInput.name ||
    parameterInput.title ||
    ''
  );
};

/**
 * Find lexicon ID by matching parameter name to lexicon title.
 * @param {string} parameterName - The parameter name from backend
 * @param {Array<{id: number, title: string}>} lexiconData
 * @returns {number|null} - The lexicon ID or null if not found
 */
export const findLexiconIdByParameterNameFromData = (parameterName, lexiconData) => {
  if (!parameterName || !lexiconData || !Array.isArray(lexiconData)) return null;
  const normalizedParam = normalize(parameterName);
  const exact = lexiconData.find(
    (e) => e?.title && normalize(e.title) === normalizedParam
  );
  if (exact) return exact.id;
  const partial = lexiconData.find((e) => {
    if (!e?.title) return false;
    const t = normalize(e.title);
    return (
      t === normalizedParam ||
      normalize(t.replace(/-/g, ' ')) === normalize(normalizedParam.replace(/-/g, ' ')) ||
      normalize(t.replace(/[.,%]/g, '')) === normalize(normalizedParam.replace(/[.,%]/g, ''))
    );
  });
  if (partial) return partial.id;
  const fuzzy = lexiconData.find((e) => {
    if (!e?.title) return false;
    const t = normalize(e.title).replace(/[.,%]/g, '').replace(/\s+/g, ' ');
    const p = normalizedParam.replace(/[.,%]/g, '').replace(/\s+/g, ' ');
    // A longer lexicon title stands for the parameter only when all it adds is a
    // beta tag ("xA" -> "xA *BETA") or the goalkeeper prefix ("Hand Passes" ->
    // "GK Hand Passes"). Any other words change what is counted: containing the
    // name used to label "Penalties" as "Goals Penalties +" and "xG" as "NPxG *BETA".
    return t === p || t === `${p} *beta` || t === `gk ${p}`;
  });
  return fuzzy ? fuzzy.id : null;
};

/**
 * Resolve parameter lexicon ID by using direct fields first, then fallback by name lookup.
 * @param {string|Object} parameterInput
 * @param {Array<{id: number, title: string}>} lexiconData
 * @returns {number|null}
 */
export const resolveParameterLexiconId = (parameterInput, lexiconData) => {
  const directLexiconId = getDirectLexiconId(parameterInput);
  if (directLexiconId !== null) {
    return directLexiconId;
  }

  const parameterName = getParameterNameFromInput(parameterInput);
  return findLexiconIdByParameterNameFromData(parameterName, lexiconData);
};

/**
 * Hook to get functions for mapping parameter names to lexicon IDs.
 * Fetches lexicon from API once (/lexicon/lexic?service=Platform&lang=en)
 * and uses the response for lookups.
 */
export const useParameterLexicon = () => {
  const [lexiconData, setLexiconData] = useState(lexiconCache);

  useEffect(() => {
    fetchLexiconOnce().then(setLexiconData);
  }, []);

  const findLexiconIdByParameterName = useCallback(
    (parameterInput) => resolveParameterLexiconId(parameterInput, lexiconData),
    [lexiconData]
  );

  const getPlayerParameterLexiconId = useCallback(
    (parameterInput) => findLexiconIdByParameterName(parameterInput),
    [findLexiconIdByParameterName]
  );

  const getTeamParameterLexiconId = useCallback(
    (parameterInput) => findLexiconIdByParameterName(parameterInput),
    [findLexiconIdByParameterName]
  );

  return useMemo(
    () => ({
      getPlayerParameterLexiconId,
      getTeamParameterLexiconId,
      findLexiconIdByParameterName,
    }),
    [
      findLexiconIdByParameterName,
      getPlayerParameterLexiconId,
      getTeamParameterLexiconId,
    ]
  );
};
